import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle,
  Ban,
  CircleCheck,
  Clock,
  RotateCw,
  Undo2,
} from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import EmptyState from '../../components/site/EmptyState';
import SEO from '../../components/SEO';
import paymentService from '../../services/paymentService';
import { getErrorMessage } from '../../utils/apiError';
import { contactInfo, getHoursSentence } from '../../configs/contactInfo';
import { useSiteConfig } from '../../configs/siteConfig';
import { toast } from '../../ui';
import PaymentOutcome from './components/PaymentOutcome';
import OrderSummaryCard, { SupportCard } from './components/OrderSummaryCard';
import UpiPaymentPanel from './components/UpiPaymentPanel';
import PaymentProofForm from './components/PaymentProofForm';
import './checkout.css';

const PAYABLE = ['PendingPayment', 'PaymentFailed'];

// React StrictMode mounts effects twice in development; share one request per order so the
// server isn't asked to start the payment twice at the same moment.
const inFlight = new Map();
const startPaymentOnce = orderId => {
  if (!inFlight.has(orderId)) {
    const request = paymentService
      .startPayment(orderId)
      .finally(() => inFlight.delete(orderId));
    inFlight.set(orderId, request);
  }
  return inFlight.get(orderId);
};

const orderLink = orderId => `/profile/order-details/${encodeURIComponent(orderId)}`;

/**
 * /checkout/pay/:orderId — pay (or check) an existing order. Works on refresh and in a new tab:
 * everything comes from POST /api/checkout/{orderId}/pay, which resumes the open attempt.
 */
const ResumePaymentPage = () => {
  useSiteConfig();
  const { orderId } = useParams();
  const [load, setLoad] = useState({ status: 'loading' });
  const [announcement, setAnnouncement] = useState('');
  const outcomeHeadingRef = useRef(null);
  const moveFocus = useRef(false);

  const start = useCallback(async () => {
    setLoad({ status: 'loading' });
    try {
      const data = await startPaymentOnce(orderId);
      setLoad({ status: 'ready', data });
    } catch (error) {
      setLoad({
        status: 'error',
        code: error?.response?.status,
        message: getErrorMessage(error, "We couldn't load the payment for this order."),
      });
    }
  }, [orderId]);

  useEffect(() => {
    start();
  }, [start]);

  const state = load.data?.orderDetails?.state;

  // After the proof is submitted, take keyboard and screen-reader users to the new message.
  useEffect(() => {
    if (moveFocus.current && outcomeHeadingRef.current) {
      outcomeHeadingRef.current.focus();
      moveFocus.current = false;
    }
  }, [state]);

  const handleSubmitted = result => {
    const nextState = result.orderState || 'PaymentSubmitted';
    moveFocus.current = true;
    setAnnouncement('Payment submitted. We verify payments by hand, typically within 12 hours.');
    toast.success('Payment submitted');
    setLoad(prev => ({
      ...prev,
      data: {
        ...prev.data,
        orderDetails: { ...prev.data.orderDetails, state: nextState },
      },
    }));
  };

  const shell = (title, subtitle, body) => (
    <main className="pk-page co-page">
      <SEO title="Payment" noindex />
      <div className="pk-container">
        <PageHeader title={title} subtitle={subtitle} />
        <p className="co-sr-only" role="status" aria-live="polite">
          {announcement}
        </p>
        {body}
      </div>
    </main>
  );

  if (load.status === 'loading') {
    return shell(
      'Complete your payment',
      `Order ${orderId}`,
      <div className="pk-split" aria-hidden="true">
        <div className="pk-stack">
          <div className="pk-skel co-skel co-skel--pay" />
          <div className="pk-skel co-skel co-skel--small" />
        </div>
        <div className="pk-skel co-skel co-skel--summary" />
      </div>
    );
  }

  if (load.status === 'error') {
    const notFound = load.code === 404;
    // 409 = the order can't be paid any more (older API versions answer this way).
    const conflict = load.code === 409;
    return shell(
      'Your payment',
      `Order ${orderId}`,
      <EmptyState
        role="alert"
        icon={<AlertCircle />}
        title={notFound ? "We couldn't find this order" : "We couldn't load this payment"}
        text={load.message}
        actions={
          <>
            {!notFound && !conflict && (
              <button
                type="button"
                className="pk-btn pk-btn--primary pk-btn--sm"
                onClick={start}
              >
                <RotateCw aria-hidden="true" />
                Retry
              </button>
            )}
            {notFound ? (
              <Link to="/profile" className="pk-btn pk-btn--secondary pk-btn--sm">
                Your orders
              </Link>
            ) : (
              <Link to={orderLink(orderId)} className="pk-btn pk-btn--secondary pk-btn--sm">
                View order
              </Link>
            )}
          </>
        }
      />
    );
  }

  const { data } = load;
  const order = data.orderDetails || {};
  const viewOrder = (
    <Link to={orderLink(orderId)} className="pk-btn pk-btn--primary pk-btn--sm">
      View order
    </Link>
  );
  const browse = (
    <Link to="/products" className="pk-btn pk-btn--secondary pk-btn--sm">
      Browse projects
    </Link>
  );

  if (!PAYABLE.includes(state)) {
    let outcome;
    switch (state) {
      case 'PaymentSubmitted':
        outcome = (
          <PaymentOutcome
            tone="info"
            icon={<Clock />}
            title="Payment under review"
            headingRef={outcomeHeadingRef}
            actions={viewOrder}
          >
            <p>
              Typically processed within 12 hours. We verify each payment by hand, then the
              project's deliverables appear on your order page.
            </p>
          </PaymentOutcome>
        );
        break;
      case 'Paid':
      case 'Fulfilled':
        outcome = (
          <PaymentOutcome
            tone="success"
            icon={<CircleCheck />}
            title="This order is paid"
            headingRef={outcomeHeadingRef}
            actions={viewOrder}
          >
            <p>
              {state === 'Fulfilled'
                ? "Your project's deliverables are on your order page."
                : "Payment received. Your project's deliverables will appear on your order page."}
            </p>
          </PaymentOutcome>
        );
        break;
      case 'Cancelled':
        outcome = (
          <PaymentOutcome
            tone="neutral"
            icon={<Ban />}
            title="This order was cancelled"
            headingRef={outcomeHeadingRef}
            actions={
              <>
                <Link to="/products" className="pk-btn pk-btn--primary pk-btn--sm">
                  Browse projects
                </Link>
                <Link to={orderLink(orderId)} className="pk-btn pk-btn--secondary pk-btn--sm">
                  View order
                </Link>
              </>
            }
          >
            <p>
              A cancelled order can't be paid. To buy this project, start a new order from its
              page.
            </p>
          </PaymentOutcome>
        );
        break;
      case 'Refunded':
        outcome = (
          <PaymentOutcome
            tone="neutral"
            icon={<Undo2 />}
            title="This order was refunded"
            headingRef={outcomeHeadingRef}
            actions={
              <>
                <Link to="/products" className="pk-btn pk-btn--primary pk-btn--sm">
                  Browse projects
                </Link>
                <Link to={orderLink(orderId)} className="pk-btn pk-btn--secondary pk-btn--sm">
                  View order
                </Link>
              </>
            }
          >
            <p>A refunded order can't be paid again.</p>
          </PaymentOutcome>
        );
        break;
      default:
        outcome = (
          <PaymentOutcome
            tone="neutral"
            icon={<AlertCircle />}
            title="This order can't be paid here"
            headingRef={outcomeHeadingRef}
            actions={
              <>
                {viewOrder}
                {browse}
              </>
            }
          >
            <p>Open the order to see its status.</p>
          </PaymentOutcome>
        );
    }

    return shell(
      'Your payment',
      `Order ${orderId}`,
      <div className="co-outcome-wrap pk-stack">
        {outcome}
        <SupportCard />
      </div>
    );
  }

  const instructions = data.paymentInstructions || {};
  const amount = order.totalAmount ?? Number(instructions.amount);
  const canPay = data.paymentFlow === 'Manual' && Boolean(instructions.upiUri) && Boolean(data.paymentId);
  // The order went back to unpaid after a failed or rejected attempt.
  const failedBefore = (order.status || []).some(s => s?.title === 'PAYMENT_FAILED');

  return shell(
    'Complete your payment',
    `Order ${orderId}`,
    <div className="pk-split">
      <div className="pk-stack">
        {failedBefore && canPay && (
          <div className="pk-alert" data-tone="warning">
            <AlertCircle aria-hidden="true" />
            <span>
              We couldn't verify an earlier payment for this order. If you've already paid, check
              the UTR in your UPI app and submit it again below. Only pay again if that payment
              didn't go through.
            </span>
          </div>
        )}

        {canPay ? (
          <>
            <UpiPaymentPanel instructions={instructions} amount={amount} orderId={orderId} />
            <PaymentProofForm paymentId={data.paymentId} onSubmitted={handleSubmitted} />
          </>
        ) : (
          <div className="pk-alert" data-tone="warning" role="alert">
            <AlertCircle aria-hidden="true" />
            <span>
              UPI payment details aren't available right now. Message us on WhatsApp at{' '}
              {contactInfo.whatsapp.display} ({getHoursSentence()}) and we'll
              help you complete this order.
            </span>
          </div>
        )}
      </div>

      <aside className="pk-sticky" aria-label="Order summary">
        <OrderSummaryCard order={order} orderId={orderId} />
      </aside>
    </div>
  );
};

export default ResumePaymentPage;
